import React, { useState, useEffect } from 'react';
import { Table, Card, Input, Space, Tag, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import request from '../../utils/request';
import type { ColumnsType } from 'antd/es/table';
import { useAuth } from '../../contexts/AuthContext';

interface EarningsHistory {
  id: number;
  wallet_address: string;
  admin_uid: number;
  earnings: string;
  interest_rate: string;
  created_at: string;
}

const EarningsHistory: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [earningsHistory, setEarningsHistory] = useState<EarningsHistory[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState({
    address: '',
  });

  const fetchEarningsHistory = async () => {
    try {
      setLoading(true);
      const response = await request.get('/admin/earnings-history/list', {
        params: {
          page: pagination.current,
          pageSize: pagination.pageSize,
          ...searchParams,
          uid: user?.id,
          level_id: user?.level_id
        }
      });
      
      if (response.code === 200) {
        setEarningsHistory(response.data.list);
        setPagination(prev => ({
          ...prev,
          total: response.data.total
        }));
      } else {
        message.error(response.message || '获取历史收益记录失败');
      }
    } catch (error) {
      console.error('获取历史收益记录失败:', error);
      message.error('获取历史收益记录失败');
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<EarningsHistory> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '钱包地址',
      dataIndex: 'wallet_address',
      key: 'wallet_address',
      width: 400,
      render: (address) => (
        <div style={{ wordBreak: 'break-all', whiteSpace: 'normal' }}>
          {address || '-'}
        </div>
      ),
    },
    {
      title: '用户ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 120,
    },
    {
      title: '收益金额',
      dataIndex: 'earnings',
      key: 'earnings',
      width: 150,
      render: (earnings) => `${Number(earnings).toFixed(6)} USDC`,
    },
    {
      title: '利率',
      dataIndex: 'interest_rate',
      key: 'interest_rate',
      width: 100,
      render: (rate) => `${Number(rate).toFixed(2)}%`,
    },
    {
      title: '记录时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time) => new Date(time).toLocaleString(),
    },
  ];

  useEffect(() => {
    fetchEarningsHistory();
  }, [pagination.current, pagination.pageSize, searchParams]);

  return (
    <Card title="历史收益记录">
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Input
            placeholder="输入钱包地址搜索"
            allowClear
            prefix={<SearchOutlined />}
            style={{ width: 300 }}
            value={searchParams.address}
            onChange={(e) => setSearchParams(prev => ({ ...prev, address: e.target.value }))}
            onPressEnter={() => fetchEarningsHistory()}
          />
          <button onClick={() => fetchEarningsHistory()}>搜索</button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={earningsHistory}
        rowKey="id"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        loading={loading}
        onChange={(newPagination) => {
          if (newPagination.current && newPagination.pageSize) {
            setPagination({
              current: newPagination.current,
              pageSize: newPagination.pageSize,
              total: pagination.total
            });
          }
        }}
      />
    </Card>
  );
};

export default EarningsHistory; 